import { AgeGroup, SportType } from "@aura-frontend/omnibus-az-client";
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  Separator,
  Skeleton,
  ValidationErrors,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { ageGroupOptions } from "@/types/game";

const sportTypeOptions: { label: string; value: SportType }[] = [
  { label: "Baseball", value: "baseball" },
  { label: "Softball", value: "softball" },
];

const moundDistanceOptions: { label: string; value: number }[] = [
  { label: "38 ft", value: 38 },
  { label: "40 ft", value: 40 },
  { label: "43 ft", value: 43 },
  { label: "44 ft", value: 44 },
  { label: "46 ft", value: 46 },
  { label: "50 ft", value: 50 },
  { label: "54 ft", value: 54 },
  { label: "60 ft 6 in", value: 60.5 },
];

export type FieldDimensions = {
  infieldAtFoulLineFeet?: number;
  fenceFirstBaseFeet?: number;
  fenceThirdBaseFeet?: number;
  diamondSizeFeet?: number;
  fenceLeftFieldFeet?: number;
  fenceCenterFieldFeet?: number;
  fenceRightFieldFeet?: number;
};

export type FieldValues = {
  sport?: SportType;
  level?: AgeGroup;
  rubberDistanceF?: number;
} & FieldDimensions;

type DimensionKey = keyof FieldDimensions;

const DIMENSION_KEYS: DimensionKey[] = [
  "infieldAtFoulLineFeet",
  "fenceFirstBaseFeet",
  "fenceThirdBaseFeet",
  "diamondSizeFeet",
  "fenceLeftFieldFeet",
  "fenceCenterFieldFeet",
  "fenceRightFieldFeet",
];

type Drafts = Record<DimensionKey, string>;

const draftsFromValues = (values?: FieldValues): Drafts =>
  DIMENSION_KEYS.reduce((acc, key) => {
    const v = values?.[key];
    acc[key] = v === undefined ? "" : String(v);
    return acc;
  }, {} as Drafts);

const parseDrafts = (drafts: Drafts): FieldDimensions => {
  const result: FieldDimensions = {};
  for (const key of DIMENSION_KEYS) {
    const raw = drafts[key];
    if (raw === "") continue;
    const num = Number(raw);
    if (Number.isFinite(num)) result[key] = num;
  }
  return result;
};

const validateDrafts = (
  drafts: Drafts,
): Partial<Record<DimensionKey, string>> => {
  const errors: Partial<Record<DimensionKey, string>> = {};
  for (const key of DIMENSION_KEYS) {
    const raw = drafts[key];
    if (raw === "") {
      errors[key] = "Required";
      continue;
    }
    const num = Number(raw);
    if (!Number.isFinite(num)) {
      errors[key] = "Must be a number";
    } else if (num <= 0) {
      errors[key] = "Must be greater than 0";
    }
  }
  return errors;
};

type FieldProps = {
  values?: FieldValues;
  onSportChange?: (sport: SportType) => void;
  onLevelChange?: (level: AgeGroup) => void;
  onMoundDistanceChange?: (moundDistance: number) => void;
  onDimensionsSave?: (dimensions: FieldDimensions) => void | Promise<unknown>;
  isLoading?: boolean;
};

function Field({
  values,
  onSportChange,
  onLevelChange,
  onMoundDistanceChange,
  onDimensionsSave,
  isLoading,
}: FieldProps) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Drafts>(() => draftsFromValues(values));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errors = useMemo(() => validateDrafts(drafts), [drafts]);
  const hasErrors = Object.keys(errors).length > 0;

  const handleOpenChange = (next: boolean) => {
    if (next) setDrafts(draftsFromValues(values));
    setOpen(next);
  };

  const setDraft = (key: DimensionKey, value: string) =>
    setDrafts((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (hasErrors) return;
    setIsSubmitting(true);
    try {
      const dimensions = parseDrafts(drafts);
      // Use Right Field for Fence - First Base and Left Field for Fence - Third Base
      if (dimensions.fenceRightFieldFeet !== undefined) {
        dimensions.fenceFirstBaseFeet = dimensions.fenceRightFieldFeet;
      }
      if (dimensions.fenceLeftFieldFeet !== undefined) {
        dimensions.fenceThirdBaseFeet = dimensions.fenceLeftFieldFeet;
      }
      await onDimensionsSave?.(dimensions);
      setOpen(false);
    } catch {
      // Keep the dialog open on failure so the user can retry; the parent
      // surfaces the error via toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="text-base font-bold w-full">Field</div>

      {isLoading ? (
        <Skeleton className="rounded h-29 w-full mt-3" />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row mt-3 gap-4">
            <div className="grow">
              <Label className="mb-2 text-sm">
                Sport
                <span className="text-error">*</span>
              </Label>

              <Select
                items={sportTypeOptions}
                value={values?.sport}
                onValueChange={(v) => onSportChange?.(v)}
                placeholder="Select"
              />
            </div>

            <div className="grow">
              <Label className="mb-2 text-sm">
                Level
                <span className="text-error">*</span>
              </Label>

              <Select
                items={ageGroupOptions}
                value={values?.level}
                onValueChange={(v) => onLevelChange?.(v)}
                placeholder="Select"
              />
            </div>

            <div className="grow">
              <Label className="mb-2 text-sm">
                Mound Distance
                <span className="text-error">*</span>
              </Label>

              <Select
                items={moundDistanceOptions}
                value={values?.rubberDistanceF}
                onValueChange={(v) => onMoundDistanceChange?.(v)}
                placeholder="Select"
              />
            </div>
          </div>

          <div className="flex justify-end mt-3">
            <Dialog
              size="lg"
              title="Edit Field Dimensions"
              open={open}
              onOpenChange={handleOpenChange}
              primaryAction={{
                label: "Save",
                onClick: handleSave,
                isLoading: isSubmitting,
                isDisabled: hasErrors,
              }}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue relative left-4"
                  isDisabled={!values?.sport}
                >
                  Edit Field Dimensions
                </Button>
              }
            >
              <div className="flex flex-col gap-2">
                <DimensionRow
                  label="Base Distance"
                  value={drafts.diamondSizeFeet}
                  error={errors.diamondSizeFeet}
                  onChange={(v) => setDraft("diamondSizeFeet", v)}
                />

                <Separator className="bg-accent-dark-2" />

                <DimensionRow
                  label="Est. Distance from Home Plate to Outfield Grass"
                  value={drafts.infieldAtFoulLineFeet}
                  error={errors.infieldAtFoulLineFeet}
                  onChange={(v) => setDraft("infieldAtFoulLineFeet", v)}
                />

                <Separator className="bg-accent-dark-2" />

                <DimensionRow
                  label="Left Field"
                  value={drafts.fenceLeftFieldFeet}
                  error={errors.fenceLeftFieldFeet}
                  onChange={(v) => setDraft("fenceLeftFieldFeet", v)}
                />

                <Separator className="bg-accent-dark-2" />

                <DimensionRow
                  label="Center Field"
                  value={drafts.fenceCenterFieldFeet}
                  error={errors.fenceCenterFieldFeet}
                  onChange={(v) => setDraft("fenceCenterFieldFeet", v)}
                />

                <Separator className="bg-accent-dark-2" />

                <DimensionRow
                  label="Right Field"
                  value={drafts.fenceRightFieldFeet}
                  error={errors.fenceRightFieldFeet}
                  onChange={(v) => setDraft("fenceRightFieldFeet", v)}
                />

                <Separator className="bg-accent-dark-2" />
              </div>
            </Dialog>
          </div>
        </>
      )}
    </div>
  );
}

type DimensionRowProps = {
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
};

function DimensionRow({ label, value, error, onChange }: DimensionRowProps) {
  return (
    <div className="grid grid-cols-[auto_1fr]">
      <div className="flex items-center text-sm">{label}</div>
      <div className="flex flex-col items-end">
        <div className="w-40">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            type="number"
            unit="FT"
            isInvalid={!!error}
          />
          {error && <ValidationErrors errors={[error]} className="mt-1" />}
        </div>
      </div>
    </div>
  );
}

export { Field };
